import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  ArrowLeft,
  Loader2,
  Lock,
  ShieldCheck,
  Package as PackageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  getStripeConfig,
  createPackagePaymentIntent,
  confirmPackagePurchase,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getInitials, formatCurrency, CATEGORY_NAMES } from '../lib/utils';
import { toast } from 'sonner';

const PackagePaymentForm = ({ purchase, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMsg('');

    const returnUrl = `${window.location.origin}/checkout/package?pi=${purchase.payment_intent_id}`;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message || 'Payment failed');
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        await confirmPackagePurchase(paymentIntent.id);
        onPaid(paymentIntent.id);
      } catch (err) {
        setErrorMsg(
          err?.response?.data?.detail ||
            'Card charged but we could not finalize the package. Support has been notified.'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="package-payment-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      {errorMsg && (
        <div
          className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3"
          data-testid="package-payment-error"
        >
          {errorMsg}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full btn-primary py-6 text-base"
        data-testid="pay-package-btn"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing…
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-2" />
            Pay {formatCurrency(purchase.amount)} &amp; activate package
          </>
        )}
      </Button>
      <p className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" />
        Payments processed by Stripe. We never store your card details.
      </p>
    </form>
  );
};

const PackageCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isClient } = useAuth();
  const state = location.state;

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState('');

  const init = useCallback(async () => {
    if (!state || !state.packageId) {
      setInitError('Missing package details. Please choose a package again.');
      setLoading(false);
      return;
    }
    try {
      const cfg = await getStripeConfig();
      const pk = cfg.data?.publishable_key;
      if (!pk) {
        setInitError('Payments are not configured on the server.');
        setLoading(false);
        return;
      }
      setStripePromise(loadStripe(pk));

      const res = await createPackagePaymentIntent(state.packageId);
      setClientSecret(res.data.client_secret);
      setPurchase({
        ...res.data,
        trainerName: state.trainerName,
        trainerImage: state.trainerImage,
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Could not start checkout';
      setInitError(typeof msg === 'string' ? msg : 'Could not start checkout');
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    if (!isAuthenticated || !isClient) {
      navigate('/login');
      return;
    }
    init();
  }, [init, isAuthenticated, isClient, navigate]);

  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: 'night',
              labels: 'floating',
              variables: {
                colorPrimary: '#C0A062',
                colorBackground: '#18181B',
                colorText: '#FAFAFA',
                colorDanger: '#f87171',
                fontFamily: 'Inter, system-ui, sans-serif',
                borderRadius: '8px',
              },
            },
          }
        : null,
    [clientSecret]
  );

  const handlePaid = () => {
    toast.success('Package activated, you can now book sessions with your credits.');
    navigate('/sessions', { state: { justPurchasedPackage: true } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="spinner" />
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen bg-[#09090B] py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-lg p-8 text-center">
          <p className="text-zinc-200 mb-6" data-testid="package-checkout-error">{initError}</p>
          <Button onClick={() => navigate('/trainers')} className="btn-primary">
            Back to trainers
          </Button>
        </div>
      </div>
    );
  }

  const pkg = purchase?.package;

  return (
    <div
      className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8"
      data-testid="package-checkout-page"
    >
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="package-checkout-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-5 gap-8">
          <aside className="lg:col-span-2 space-y-4">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6">
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold mb-4 flex items-center gap-2">
                <PackageIcon className="w-4 h-4 text-[#C0A062]" />
                Package summary
              </h2>

              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-800">
                <Avatar className="w-12 h-12 border border-zinc-700">
                  <AvatarImage src={purchase?.trainerImage} alt={purchase?.trainerName} />
                  <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-sm font-semibold">
                    {getInitials(purchase?.trainerName || 'T')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-white font-semibold">{purchase?.trainerName}</p>
                  <p className="text-xs text-zinc-400">{CATEGORY_NAMES[pkg?.category] || pkg?.category}</p>
                </div>
              </div>

              <div className="space-y-1 text-sm">
                <p className="text-white font-semibold">{pkg?.name}</p>
                <p className="text-zinc-400">{pkg?.session_count} sessions included</p>
                {pkg?.description && (
                  <p className="text-zinc-500 text-xs whitespace-pre-line mt-2">{pkg.description}</p>
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-zinc-800 space-y-2">
                <div className="flex items-center justify-between text-sm text-zinc-400">
                  <span>Bundle total</span>
                  <span>{formatCurrency(purchase?.amount || 0)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                  <span className="text-white font-semibold">Total today</span>
                  <span className="text-[#C0A062] text-xl font-bold" data-testid="package-checkout-total">
                    {formatCurrency(purchase?.amount || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-[#18181B] border border-emerald-500/20 rounded-lg p-4 text-xs text-zinc-300 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>You&apos;ll be charged the full bundle now. Redeem one credit each time you book a session.</span>
            </div>
          </aside>

          <section className="lg:col-span-3">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6">
              <h1 className="text-2xl font-bold text-white mb-1">Payment</h1>
              <p className="text-sm text-zinc-400 mb-6">
                Enter your card details to activate this package.
              </p>

              {clientSecret && stripePromise && elementsOptions ? (
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <PackagePaymentForm purchase={purchase} onPaid={handlePaid} />
                </Elements>
              ) : (
                <div className="flex items-center justify-center py-10 text-zinc-500">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Preparing secure form…
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PackageCheckout;
